// „KINEK" + FELHASZNÁLÁS HELYE — az eszköz-leltár űrlap és az igénylés-űrlap KÖZÖS mezőcsoportja (Ilcsa #114,
// #122). A szabály a contractban él (`refineOfficeRule`), ez a felület őszinte tükre:
//   • IRODA (általános használat): nincs személy, a hely fixen „közösségi" — a select tiltva mutatja;
//   • SZEMÉLY: munkatárs-választó, a hely csak iroda / otthon / vegyes (vagy nincs megadva).
// A csapat NEM mező: a szerver a személy beosztásából számolja, itt nincs választó.
import { useEffect } from 'react';
import { useFormContext, useWatch, type FieldValues, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type ComboboxOption } from '@/components/shared/entity-combobox';
import { ComboboxField, SelectField, SwitchField } from '@/components/shared/form-fields';
import { NONE, OFFICE_USAGE, PERSONAL_USAGE_OPTIONS } from '@/features/assets/form';
import { employeeSearch } from '@/features/employees/search';

/** A két űrlap közös mező-alakja (a személy-mező neve eltér: `holderIdentityId` / `requestedForIdentityId`). */
interface RecipientValues extends FieldValues {
  officeUse: boolean;
  usageLocation: string;
}

export interface AssetRecipientFieldsProps<TValues extends RecipientValues> {
  /** A személy-választó mezője (birtokos / „kinek"). */
  personField: Path<TValues>;
  personLabel: string;
  personPlaceholder: string;
  personHint?: string;
  /** A személy KÖTELEZŐ-e (leltár: igen; igénynél üresen magának igényel). */
  personRequired?: boolean;
  /** A szerkesztett sor jelenlegi személye (a választó előtöltéséhez). */
  initialPerson?: ComboboxOption | null;
}

export function AssetRecipientFields<TValues extends RecipientValues>({
  personField,
  personLabel,
  personPlaceholder,
  personHint,
  personRequired = false,
  initialPerson = null,
}: AssetRecipientFieldsProps<TValues>): JSX.Element {
  const { t, i18n } = useTranslation();
  const { control, setValue, getValues } = useFormContext<TValues>();
  // A mező-nevek a két űrlap közös alakjából jönnek; a literál a generikus `Path<TValues>`-ra nem szűkül.
  const officeUseName = 'officeUse' as Path<TValues>;
  const usageName = 'usageLocation' as Path<TValues>;
  const officeUse = useWatch({ control, name: officeUseName }) === true;

  // Az iroda-váltás UGYANABBAN a pillanatban igazítja a függő mezőket: a személyt üríti, a helyet fixre állítja;
  // visszaváltáskor a `social` (személynél érvénytelen) helyet törli — így a form sosem marad elutasított állapotban.
  useEffect(() => {
    if (officeUse) {
      setValue(personField, '' as never);
      setValue(usageName, OFFICE_USAGE as never);
    } else if (getValues(usageName) === OFFICE_USAGE) {
      setValue(usageName, NONE as never);
    }
  }, [officeUse, personField, usageName, setValue, getValues]);

  const usageOptions = officeUse
    ? [{ value: OFFICE_USAGE, label: t(`assets.usageLabel.${OFFICE_USAGE}`) }]
    : [
        { value: NONE, label: t('assets.fields.usageNone') },
        ...PERSONAL_USAGE_OPTIONS.map((v) => ({ value: v, label: t(`assets.usageLabel.${v}`) })),
      ];

  return (
    <>
      <SwitchField<TValues>
        name={officeUseName}
        label={t('assets.fields.officeUse')}
        hint={t('assets.fields.officeUseHint')}
      />
      {!officeUse && (
        <ComboboxField<TValues>
          name={personField}
          label={personLabel}
          required={personRequired}
          search={employeeSearch(i18n.language.toLowerCase().startsWith('en') ? 'en' : 'hu')}
          initialOption={initialPerson}
          placeholder={personPlaceholder}
          {...(personHint ? { hint: personHint } : {})}
        />
      )}
      <SelectField<TValues>
        name={usageName}
        label={t('assets.fields.usageLocation')}
        options={usageOptions}
        disabled={officeUse}
        {...(officeUse ? { hint: t('assets.fields.usageOfficeFixed') } : {})}
      />
    </>
  );
}
